import React from 'react';
import { Box, CircularProgress, Grid, Typography, Chip, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Drawer, IconButton, Modal } from '@mui/material';
import { useQueryCoordinate } from '../../clients/graphql/graphql.hooks';
import { useParams, Link } from 'react-router-dom';
import { Close as CloseIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer
} from 'recharts';
import { 
    userPilotDetailsQuery,
} from '../../clients/graphql/pages/users-queries';

export const BalancingStatesPage: React.FC = () => {
    const { autoPilotSettingsKey } = useParams();
    const [selectedState, setSelectedState] = React.useState<any>(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedActivity, setSelectedActivity] = React.useState<any>(null);
    const [balanceDecisionsModalOpen, setBalanceDecisionsModalOpen] = React.useState(false);
    const [activityDataModalOpen, setActivityDataModalOpen] = React.useState(false);
    const [balancingStateDataModalOpen, setBalancingStateDataModalOpen] = React.useState(false);
    const [copiedActivity, setCopiedActivity] = React.useState(false);
    const [copiedState, setCopiedState] = React.useState(false);

    const { loading, error, data } = useQueryCoordinate(userPilotDetailsQuery, {
        variables: {
            autoPilotSettingsKey: parseInt(autoPilotSettingsKey as string)
        },
    });

    const handleRowClick = (state: any) => {
        setSelectedState(state);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedState(null);
    };

    const handleBalanceDecisionsClick = (activity: any) => {
        setSelectedActivity(activity);
        setBalanceDecisionsModalOpen(true);
    };

    const handleCloseBalanceDecisionsModal = () => {
        setBalanceDecisionsModalOpen(false);
        setSelectedActivity(null);
    };

    const handleActivityDataClick = (activity: any) => {
        setSelectedActivity(activity);
        setActivityDataModalOpen(true);
    };

    const handleCloseActivityDataModal = () => {
        setActivityDataModalOpen(false);
        setSelectedActivity(null);
    };

    const handleBalancingStateDataClick = () => {
        setBalancingStateDataModalOpen(true);
    };

    const handleCloseBalancingStateDataModal = () => {
        setBalancingStateDataModalOpen(false);
    };

    const handleCopyActivityData = async () => {
        if (!selectedActivity?.data) return;
        try {
            const jsonString = JSON.stringify(selectedActivity.data, null, 2).replace(/\n+$/, '');
            await navigator.clipboard.writeText(jsonString);
            setCopiedActivity(true);
            setTimeout(() => setCopiedActivity(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleCopyStateData = async () => {
        if (!selectedState?.data) return;
        try {
            const jsonString = JSON.stringify(selectedState.data, null, 2).replace(/\n+$/, '');
            await navigator.clipboard.writeText(jsonString);
            setCopiedState(true);
            setTimeout(() => setCopiedState(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Debug logging
    console.log('BalancingStatesPage Debug:', {
        autoPilotSettingsKey,
        loading,
        error,
        data,
        autoPilotSettings: data?.slaveAutoPilotPortfoliosGet?.slaveAutoPilotPortfolios,
        balancingStates: data?.slaveAutoPilotPortfoliosGet?.slaveAutoPilotPortfolios?.[0]?.autoPilotSettings?.balancingStatesConnection?.balancingStates
    });

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '50vh',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
            }}>
                <CircularProgress size={60} sx={{ color: '#3b82f6' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ 
                maxWidth: 1200, 
                mx: 'auto', 
                mt: 4, 
                px: 2,
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                minHeight: '100vh'
            }}>
                <Alert severity="error" sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    Error loading balancing states. Please reload the page.
                </Alert>
            </Box>
        );
    }

    const autoPilotSettings = data?.slaveAutoPilotPortfoliosGet?.slaveAutoPilotPortfolios || [];
    const balancingStates = autoPilotSettings[0]?.autoPilotSettings?.balancingStatesConnection?.balancingStates || [];
    const totalCount = autoPilotSettings[0]?.autoPilotSettings?.balancingStatesConnection?.total || 0;

    return (
        <Box sx={{ 
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            minHeight: '100vh',
            py: 4
        }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 4,
                    p: 3,
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(20px)'
                }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#1e293b', mb: 1 }}>
                            Balancing States
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                            Settings Key: {autoPilotSettingsKey} • {totalCount} total states
                        </Typography>
                    </Box>
                    <Chip 
                        label={`${totalCount} States`}
                        sx={{ 
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: 32
                        }}
                    />
                </Box>

                {/* Auto Pilot Settings Card */}
                <Box sx={{ 
                    background: 'rgba(255,255,255,0.9)', 
                    borderRadius: 4, 
                    p: 3, 
                    mb: 4,
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(20px)'
                }}>
                    {autoPilotSettings.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                                No auto pilot settings found with key: {autoPilotSettingsKey}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2.5, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                AUTO PILOT SETTINGS
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Settings Key
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            {autoPilotSettings[0].autoPilotSettingsKey}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Master User
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            {autoPilotSettings[0].masterPortfolio.userFullName}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Slave User
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            {autoPilotSettings[0].slaveUserKey}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Allocation
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            ${autoPilotSettings[0].autoPilotSettings.allocationAmount?.toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Status
                                        </Typography>
                                        <Chip 
                                            label={autoPilotSettings[0].autoPilotSettings.setupStatus} 
                                            size="small"
                                            sx={{ 
                                                height: 24, 
                                                fontSize: '0.75rem',
                                                background: autoPilotSettings[0].autoPilotSettings.setupStatus === 'ACTIVE' ? '#dcfce7' : 
                                                           autoPilotSettings[0].autoPilotSettings.setupStatus === 'INITIATED' ? '#dbeafe' : 
                                                           autoPilotSettings[0].autoPilotSettings.setupStatus === 'PAUSED' ? '#fef3c7' : '#f1f5f9',
                                                color: autoPilotSettings[0].autoPilotSettings.setupStatus === 'ACTIVE' ? '#166534' : 
                                                       autoPilotSettings[0].autoPilotSettings.setupStatus === 'INITIATED' ? '#1e40af' : 
                                                       autoPilotSettings[0].autoPilotSettings.setupStatus === 'PAUSED' ? '#92400e' : '#64748b',
                                                fontWeight: 600,
                                                alignSelf: 'flex-start'
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Created
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            {new Date(autoPilotSettings[0].autoPilotSettings.initiatedAt).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Box>

                {/* Balancing States Table */}
                <Box sx={{ 
                    background: 'rgba(255,255,255,0.9)', 
                    borderRadius: 4, 
                    p: 3,
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(20px)'
                }}>
                    {balancingStates.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                                No balancing states found for this auto pilot settings.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 3, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                BALANCING STATES
                            </Typography>
                            <Box sx={{ 
                                background: 'rgba(248,250,252,0.8)', 
                                borderRadius: 3, 
                                overflow: 'hidden',
                                border: '1px solid rgba(0,0,0,0.04)'
                            }}>
                                <TableContainer>
                                    <Table sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
                                        <TableHead>
                                            <TableRow sx={{ background: 'rgba(241,245,249,0.8)' }}>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>State</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Work Required</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Workflow</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Created At</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {balancingStates.map((state: any, index: number) => (
                                                <TableRow 
                                                    key={index} 
                                                    sx={{ 
                                                        '&:nth-of-type(even)': { background: 'rgba(255,255,255,0.4)' },
                                                        cursor: 'pointer',
                                                        '&:hover': { 
                                                            background: 'rgba(241,245,249,0.8)',
                                                            transform: 'translateY(-1px)',
                                                            transition: 'all 0.2s ease-in-out'
                                                        },
                                                        transition: 'all 0.2s ease-in-out'
                                                    }} 
                                                    onClick={() => handleRowClick(state)}
                                                >
                                                    <TableCell sx={{ py: 2, px: 2 }}>
                                                        <Chip 
                                                            label={state.stateType || 'Unknown'} 
                                                            size="small"
                                                            sx={{ 
                                                                height: 24, 
                                                                fontSize: '0.75rem',
                                                                background: state.stateType === 'TERMINAL' ? '#dcfce7' : '#fef3c7',
                                                                color: state.stateType === 'TERMINAL' ? '#166534' : '#92400e',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2, px: 2 }}>
                                                        <Chip 
                                                            label={state.workRequiredAttributionStateType || 'Unknown'} 
                                                            size="small"
                                                            sx={{ 
                                                                height: 24, 
                                                                fontSize: '0.75rem',
                                                                background: state.workRequiredAttributionStateType === 'NO_WORK_REQUIRED' ? '#dcfce7' : 
                                                                           state.workRequiredAttributionStateType === 'PLATFORM' ? '#dbeafe' : 
                                                                           state.workRequiredAttributionStateType === 'USER' ? '#fef3c7' : 
                                                                           state.workRequiredAttributionStateType === 'BROKER' ? '#fee2e2' : '#f1f5f9',
                                                                color: state.workRequiredAttributionStateType === 'NO_WORK_REQUIRED' ? '#166534' : 
                                                                       state.workRequiredAttributionStateType === 'PLATFORM' ? '#1e40af' : 
                                                                       state.workRequiredAttributionStateType === 'USER' ? '#92400e' : 
                                                                       state.workRequiredAttributionStateType === 'BROKER' ? '#dc2626' : '#64748b',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2, px: 2 }}>
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small"
                                                            component={Link}
                                                            to={`/developer/workflows/BalancingLegacyWorkflow/executions/${state.workflowId}`}
                                                            sx={{ 
                                                                textTransform: 'none',
                                                                fontSize: '0.75rem',
                                                                height: 28,
                                                                borderColor: '#3b82f6',
                                                                color: '#3b82f6',
                                                                '&:hover': {
                                                                    borderColor: '#1d4ed8',
                                                                    background: 'rgba(59,130,246,0.04)'
                                                                }
                                                            }}
                                                        >
                                                            View Workflow
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem', py: 2, px: 2, color: '#64748b', fontWeight: 500 }}>
                                                        {new Date(state.createdAt).toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{
                    sx: {
                        width: '70%',
                        boxSizing: 'border-box',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        borderLeft: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
                    },
                }}
            >
                {selectedState ? (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Header */}
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            p: 3, 
                            pb: 2,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            background: 'rgba(255,255,255,0.8)',
                            backdropFilter: 'blur(20px)'
                        }}>
                            <Box>
                                <Typography variant="h5" fontWeight={600} sx={{ color: '#1e293b', mb: 0.5 }}>
                                    Balancing State Details
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {selectedState.stateType} • {new Date(selectedState.createdAt).toLocaleDateString()}
                                </Typography>
                            </Box>
                            <IconButton 
                                onClick={handleCloseDrawer}
                                sx={{ 
                                    color: '#64748b',
                                    '&:hover': { 
                                        background: 'rgba(0,0,0,0.04)',
                                        color: '#1e293b'
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        
                        {/* Content */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                            {/* State Details Card */}
                            <Box sx={{ 
                                background: 'rgba(255,255,255,0.9)', 
                                borderRadius: 3, 
                                p: 2.5, 
                                mb: 3,
                                border: '1px solid rgba(0,0,0,0.04)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                            }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem' }}>
                                    STATE INFORMATION
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', minWidth: 80 }}>
                                                State:
                                            </Typography>
                                            <Chip 
                                                label={selectedState.stateType || 'N/A'} 
                                                size="small"
                                                sx={{ 
                                                    height: 20, 
                                                    fontSize: '0.7rem',
                                                    background: selectedState.stateType === 'TERMINAL' ? '#dcfce7' : '#fef3c7',
                                                    color: selectedState.stateType === 'TERMINAL' ? '#166534' : '#92400e',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', minWidth: 80 }}>
                                                Work Required:
                                            </Typography>
                                            <Chip 
                                                label={selectedState.workRequiredAttributionStateType || 'N/A'} 
                                                size="small"
                                                sx={{ 
                                                    height: 20, 
                                                    fontSize: '0.7rem',
                                                    background: selectedState.workRequiredAttributionStateType === 'NO_WORK_REQUIRED' ? '#dcfce7' : '#fef3c7',
                                                    color: selectedState.workRequiredAttributionStateType === 'NO_WORK_REQUIRED' ? '#166534' : '#92400e',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            <strong>Workflow ID:</strong> {selectedState.workflowId || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            <strong>Created:</strong> {new Date(selectedState.createdAt).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                {/* View Data Button */}
                                {selectedState.data && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleBalancingStateDataClick}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                height: 32,
                                                borderColor: '#3b82f6',
                                                color: '#3b82f6',
                                                '&:hover': {
                                                    borderColor: '#1d4ed8',
                                                    background: 'rgba(59,130,246,0.04)'
                                                }
                                            }}
                                        >
                                            View State Data
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {/* Balancing Activities */}
                            {selectedState.autoPilotBalancingActivities && selectedState.autoPilotBalancingActivities.length > 0 ? (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem' }}>
                                        BALANCING ACTIVITIES ({selectedState.autoPilotBalancingActivities.length})
                                    </Typography>
                                    {selectedState.autoPilotBalancingActivities.map((activity: any, index: number) => (
                                        <Box key={index} sx={{ 
                                            background: 'rgba(255,255,255,0.9)', 
                                            borderRadius: 3, 
                                            p: 2.5, 
                                            mb: 2,
                                            border: '1px solid rgba(0,0,0,0.04)',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b', fontSize: '0.875rem' }}>
                                                    Activity {index + 1}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {activity.balanceDecisions && activity.balanceDecisions.length > 0 && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleBalanceDecisionsClick(activity);
                                                            }}
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontSize: '0.7rem',
                                                                height: 24,
                                                                borderColor: '#10b981',
                                                                color: '#10b981',
                                                                '&:hover': {
                                                                    borderColor: '#059669',
                                                                    background: 'rgba(16,185,129,0.04)'
                                                                }
                                                            }}
                                                        >
                                                            View Decisions ({activity.balanceDecisions.length})
                                                        </Button>
                                                    )}
                                                    {activity.data && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleActivityDataClick(activity);
                                                            }}
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontSize: '0.7rem',
                                                                height: 24,
                                                                borderColor: '#3b82f6',
                                                                color: '#3b82f6',
                                                                '&:hover': {
                                                                    borderColor: '#1d4ed8',
                                                                    background: 'rgba(59,130,246,0.04)'
                                                                }
                                                            }}
                                                        >
                                                            View Data
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                            
                                            {/* Activity Metrics */}
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            Slave Valuation
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                                            ${activity.slaveValuation?.toLocaleString() || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            Position Market Value
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                                            ${activity.positionMarketValueAmount?.toLocaleString() || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            Distance to Target
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                                            {activity.distanceToTargetPositions?.toFixed(4) || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            Created
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                                            {new Date(activity.createdAt).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            {/* Auto Pilot Orders */}
                                            {activity.autoPilotOrders && activity.autoPilotOrders.length > 0 ? (
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b', mb: 1.5, fontSize: '0.8rem' }}>
                                                        Orders ({activity.autoPilotOrders.length})
                                                    </Typography>
                                                    <Box sx={{ 
                                                        background: 'rgba(248,250,252,0.8)', 
                                                        borderRadius: 2, 
                                                        overflow: 'hidden',
                                                        border: '1px solid rgba(0,0,0,0.04)'
                                                    }}>
                                                        <TableContainer>
                                                            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
                                                                <TableHead>
                                                                    <TableRow sx={{ background: 'rgba(241,245,249,0.8)' }}>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', py: 1, px: 1.5 }}>Symbol</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', py: 1, px: 1.5 }}>Side</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', py: 1, px: 1.5 }}>Quantity</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', py: 1, px: 1.5 }}>Status</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', py: 1, px: 1.5 }}>State</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', py: 1, px: 1.5 }}>Reason</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {activity.autoPilotOrders.map((order: any, orderIndex: number) => (
                                                                        <TableRow 
                                                                            key={orderIndex}
                                                                            sx={{ 
                                                                                '&:nth-of-type(even)': { background: 'rgba(255,255,255,0.4)' },
                                                                                '&:hover': { background: 'rgba(241,245,249,0.6)' }
                                                                            }}
                                                                        >
                                                                            <TableCell sx={{ fontSize: '0.75rem', py: 1, px: 1.5, fontWeight: 500 }}>{order.symbol}</TableCell>
                                                                            <TableCell sx={{ fontSize: '0.75rem', py: 1, px: 1.5 }}>{order.orderSide}</TableCell>
                                                                            <TableCell sx={{ fontSize: '0.75rem', py: 1, px: 1.5 }}>{order.quantity}</TableCell>
                                                                            <TableCell sx={{ py: 1, px: 1.5 }}>
                                                                                <Chip 
                                                                                    label={order.orderStatus} 
                                                                                    size="small"
                                                                                    sx={{ 
                                                                                        height: 18, 
                                                                                        fontSize: '0.65rem',
                                                                                        background: order.orderStatus === 'SENT_TO_BROKER' ? '#dcfce7' : 
                                                                                                   order.orderStatus === 'FAILED' ? '#fee2e2' : '#f1f5f9',
                                                                                        color: order.orderStatus === 'SENT_TO_BROKER' ? '#166534' : 
                                                                                               order.orderStatus === 'FAILED' ? '#dc2626' : '#64748b',
                                                                                        fontWeight: 500
                                                                                    }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell sx={{ py: 1, px: 1.5 }}>
                                                                                <Chip 
                                                                                    label={order.orderState || 'N/A'} 
                                                                                    size="small"
                                                                                    sx={{ 
                                                                                        height: 18, 
                                                                                        fontSize: '0.65rem',
                                                                                        background: order.orderState === 'FILLED' ? '#dcfce7' : 
                                                                                                   order.orderState === 'CANCELED' ? '#fee2e2' : 
                                                                                                   order.orderState === 'PENDING' ? '#fef3c7' : '#f1f5f9',
                                                                                        color: order.orderState === 'FILLED' ? '#166534' : 
                                                                                               order.orderState === 'CANCELED' ? '#dc2626' : 
                                                                                               order.orderState === 'PENDING' ? '#92400e' : '#64748b',
                                                                                        fontWeight: 500
                                                                                    }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell sx={{ fontSize: '0.75rem', py: 1, px: 1.5, color: '#64748b' }}>
                                                                                {order.failureReason || 'N/A'}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                                                    No orders found for this activity.
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                                    No balancing activities found for this state.
                                </Typography>
                            )}
                        </Box>
                        
                        {/* Chart Section */}
                        {selectedState.autoPilotBalancingActivities && selectedState.autoPilotBalancingActivities.length > 0 && (
                            <Box sx={{ mt: 4 }}>
                                <Box sx={{ 
                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                                    borderRadius: 4, 
                                    p: 3,
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.8)',
                                    backdropFilter: 'blur(20px)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '1px',
                                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
                                    }
                                }}>
                                    {/* Header */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between', 
                                        mb: 3,
                                        pb: 2,
                                        borderBottom: '1px solid rgba(0,0,0,0.06)'
                                    }}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700} sx={{ 
                                                color: '#1e293b', 
                                                fontSize: '1.1rem',
                                                letterSpacing: '-0.02em',
                                                mb: 0.5
                                            }}>
                                                Portfolio Value Trends
                                            </Typography>
                                            <Typography variant="body2" sx={{ 
                                                color: '#64748b', 
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }}>
                                                Position market value vs. cash holdings over time
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1.5,
                                            p: 1,
                                            background: 'rgba(255,255,255,0.6)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(0,0,0,0.04)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ 
                                                    width: 8, 
                                                    height: 8, 
                                                    borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                    boxShadow: '0 2px 4px rgba(59,130,246,0.3)'
                                                }} />
                                                <Typography variant="caption" sx={{ 
                                                    fontSize: '0.7rem', 
                                                    fontWeight: 600, 
                                                    color: '#1e293b' 
                                                }}>
                                                    Positions
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ 
                                                    width: 8, 
                                                    height: 8, 
                                                    borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    boxShadow: '0 2px 4px rgba(16,185,129,0.3)'
                                                }} />
                                                <Typography variant="caption" sx={{ 
                                                    fontSize: '0.7rem', 
                                                    fontWeight: 600, 
                                                    color: '#1e293b' 
                                                }}>
                                                    Cash
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Chart Container */}
                                    <Box sx={{ 
                                        position: 'relative',
                                        height: 320,
                                        background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%)',
                                        borderRadius: 3,
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'radial-gradient(circle at 30% 20%, rgba(59,130,246,0.03) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(16,185,129,0.03) 0%, transparent 50%)',
                                            pointerEvents: 'none'
                                        }
                                    }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={selectedState.autoPilotBalancingActivities.map((activity: any, index: number) => ({
                                                    name: `Activity ${index + 1}`,
                                                    timestamp: new Date(activity.createdAt).toLocaleString(),
                                                    positionMarketValue: activity.positionMarketValueAmount || 0,
                                                    slaveCashAmount: activity.slaveCashPositionAmount || 0,
                                                    date: new Date(activity.createdAt)
                                                })).sort((a: any, b: any) => a.date.getTime() - b.date.getTime())}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                            >
                                                <defs>
                                                    <linearGradient id="positionGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                                    </linearGradient>
                                                    <linearGradient id="cashGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.8}/>
                                                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.1}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid 
                                                    strokeDasharray="3 3" 
                                                    stroke="rgba(0,0,0,0.06)" 
                                                    strokeWidth={0.5}
                                                />
                                                <XAxis 
                                                    dataKey="timestamp" 
                                                    stroke="#94a3b8"
                                                    fontSize={11}
                                                    fontWeight={500}
                                                    angle={-45}
                                                    textAnchor="end"
                                                    height={70}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    tick={{ fill: '#64748b' }}
                                                />
                                                <YAxis 
                                                    stroke="#94a3b8" 
                                                    fontSize={11}
                                                    fontWeight={500}
                                                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    tick={{ fill: '#64748b' }}
                                                />
                                                <Tooltip 
                                                    contentStyle={{
                                                        background: 'rgba(255,255,255,0.98)',
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                        boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.1)',
                                                        backdropFilter: 'blur(20px)',
                                                        padding: '12px 16px',
                                                        fontSize: '12px',
                                                        fontWeight: 500
                                                    }}
                                                    formatter={(value: any, name: any) => [
                                                        `$${value?.toLocaleString()}`, 
                                                        name === 'positionMarketValue' ? 'Position Value' : 'Cash Amount'
                                                    ]}
                                                    labelFormatter={(label) => `📅 ${label}`}
                                                    cursor={{
                                                        stroke: 'rgba(0,0,0,0.1)',
                                                        strokeWidth: 1,
                                                        strokeDasharray: '3 3'
                                                    }}
                                                />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="positionMarketValue" 
                                                    stroke="url(#positionGradient)" 
                                                    strokeWidth={3}
                                                    dot={{ 
                                                        fill: '#3b82f6', 
                                                        stroke: '#ffffff', 
                                                        strokeWidth: 2, 
                                                        r: 5,
                                                        filter: 'drop-shadow(0 2px 4px rgba(59,130,246,0.3))'
                                                    }}
                                                    activeDot={{ 
                                                        r: 8, 
                                                        stroke: '#3b82f6', 
                                                        strokeWidth: 3,
                                                        fill: '#ffffff',
                                                        filter: 'drop-shadow(0 4px 8px rgba(59,130,246,0.4))'
                                                    }}
                                                    name="Position Market Value"
                                                />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="slaveCashAmount" 
                                                    stroke="url(#cashGradient)" 
                                                    strokeWidth={3}
                                                    dot={{ 
                                                        fill: '#10b981', 
                                                        stroke: '#ffffff', 
                                                        strokeWidth: 2, 
                                                        r: 5,
                                                        filter: 'drop-shadow(0 2px 4px rgba(16,185,129,0.3))'
                                                    }}
                                                    activeDot={{ 
                                                        r: 8, 
                                                        stroke: '#10b981', 
                                                        strokeWidth: 3,
                                                        fill: '#ffffff',
                                                        filter: 'drop-shadow(0 4px 8px rgba(16,185,129,0.4))'
                                                    }}
                                                    name="Slave Cash Amount"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>

                                    {/* Summary Stats */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        mt: 3,
                                        pt: 3,
                                        borderTop: '1px solid rgba(0,0,0,0.06)'
                                    }}>
                                        {(() => {
                                            const activities = selectedState.autoPilotBalancingActivities;
                                            const latestActivity = activities[activities.length - 1];
                                            const firstActivity = activities[0];
                                            
                                            const positionChange = latestActivity?.positionMarketValueAmount - firstActivity?.positionMarketValueAmount;
                                            const cashChange = latestActivity?.slaveCashPositionAmount - firstActivity?.slaveCashPositionAmount;
                                            
                                            return (
                                                <>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="caption" sx={{ 
                                                            color: '#64748b', 
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Position Change
                                                        </Typography>
                                                        <Typography variant="h6" sx={{ 
                                                            color: positionChange >= 0 ? '#10b981' : '#ef4444',
                                                            fontWeight: 700,
                                                            fontSize: '1rem',
                                                            mt: 0.5
                                                        }}>
                                                            {positionChange >= 0 ? '+' : ''}${positionChange?.toLocaleString() || '0'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="caption" sx={{ 
                                                            color: '#64748b', 
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Cash Change
                                                        </Typography>
                                                        <Typography variant="h6" sx={{ 
                                                            color: cashChange >= 0 ? '#10b981' : '#ef4444',
                                                            fontWeight: 700,
                                                            fontSize: '1rem',
                                                            mt: 0.5
                                                        }}>
                                                            {cashChange >= 0 ? '+' : ''}${cashChange?.toLocaleString() || '0'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="caption" sx={{ 
                                                            color: '#64748b', 
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Total Activities
                                                        </Typography>
                                                        <Typography variant="h6" sx={{ 
                                                            color: '#1e293b',
                                                            fontWeight: 700,
                                                            fontSize: '1rem',
                                                            mt: 0.5
                                                        }}>
                                                            {activities.length}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            );
                                        })()}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </Drawer>

            {/* Balance Decisions Modal */}
            <Modal
                open={balanceDecisionsModalOpen}
                onClose={handleCloseBalanceDecisionsModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box sx={{
                    width: '90%',
                    maxWidth: 1200,
                    maxHeight: '90vh',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Modal Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        background: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(20px)'
                    }}>
                        <Box>
                            <Typography variant="h5" fontWeight={600} sx={{ color: '#1e293b', mb: 0.5 }}>
                                Balance Decisions
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                {selectedActivity?.balanceDecisions?.length || 0} decisions • {new Date(selectedActivity?.createdAt).toLocaleDateString()}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseBalanceDecisionsModal}
                            sx={{
                                color: '#64748b',
                                '&:hover': {
                                    background: 'rgba(0,0,0,0.04)',
                                    color: '#1e293b'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Modal Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {selectedActivity?.balanceDecisions && selectedActivity.balanceDecisions.length > 0 ? (
                            <Box>
                                {selectedActivity.balanceDecisions.map((decision: any, index: number) => {
                                    const calculatedAmount = decision.quantity && decision.price ? (decision.quantity * decision.price) : 0;
                                    const actionText = `${decision.orderSide || 'UNKNOWN'} ${decision.symbol || 'N/A'}`;
                                    
                                    return (
                                        <Box key={index} sx={{
                                            background: 'rgba(255,255,255,0.9)',
                                            borderRadius: 2,
                                            p: 1.5,
                                            mb: 1,
                                            border: '1px solid rgba(0,0,0,0.04)',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b', fontSize: '0.8rem' }}>
                                                    {actionText}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                        ${calculatedAmount.toFixed(2)}
                                                    </Typography>
                                                    <Chip
                                                        label={decision.isExecuteOrderDecision ? 'Execute' : 'No Execute'}
                                                        size="small"
                                                        sx={{
                                                            height: 16,
                                                            fontSize: '0.6rem',
                                                            background: decision.isExecuteOrderDecision ? '#dcfce7' : '#f1f5f9',
                                                            color: decision.isExecuteOrderDecision ? '#166534' : '#64748b',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            
                                            <Grid container spacing={1}>
                                                <Grid item xs={3}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Quantity
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            {decision.quantity || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Price
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            ${decision.price?.toFixed(2) || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Kind
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            {decision.kind || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Position Type
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            {decision.positionType || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Position Effect
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            {decision.positionEffect || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Option Type
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            {decision.optionDetails?.optionType || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            Reason
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.7rem', lineHeight: 1.3 }}>
                                                            {decision.reason || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                    No balance decisions found for this activity.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Modal>

            {/* Activity Data Modal */}
            <Modal
                open={activityDataModalOpen}
                onClose={handleCloseActivityDataModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box sx={{
                    width: '90%',
                    maxWidth: 1200,
                    maxHeight: '90vh',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Modal Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        background: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(20px)'
                    }}>
                        <Box>
                            <Typography variant="h5" fontWeight={600} sx={{ color: '#1e293b', mb: 0.5 }}>
                                Activity Data
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                {new Date(selectedActivity?.createdAt).toLocaleDateString()}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseActivityDataModal}
                            sx={{
                                color: '#64748b',
                                '&:hover': {
                                    background: 'rgba(0,0,0,0.04)',
                                    color: '#1e293b'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Modal Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {selectedActivity?.data ? (
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem' }}>
                                    ACTIVITY DATA (JSON)
                                </Typography>
                                <Box sx={{
                                    position: 'relative',
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: 3,
                                    p: 2.5,
                                    border: '1px solid rgba(0,0,0,0.04)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyActivityData}
                                        sx={{
                                            position: 'absolute',
                                            top: '0.5rem',
                                            right: '0.5rem',
                                            color: copiedActivity ? '#4caf50' : '#666',
                                            backgroundColor: 'white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0,0,0,0.04)'
                                            }
                                        }}
                                        title={copiedActivity ? 'Copied!' : 'Copy to clipboard'}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                    <pre style={{
                                        margin: 0,
                                        fontSize: '0.8rem',
                                        lineHeight: 1.5,
                                        color: '#1e293b',
                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word'
                                    }}>
                                        {JSON.stringify(selectedActivity.data, null, 2).replace(/\n+$/, '')}
                                    </pre>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                    No data found for this activity.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Modal>

            {/* Balancing State Data Modal */}
            <Modal
                open={balancingStateDataModalOpen}
                onClose={handleCloseBalancingStateDataModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box sx={{
                    width: '90%',
                    maxWidth: 1200,
                    maxHeight: '90vh',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Modal Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        background: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(20px)'
                    }}>
                        <Box>
                            <Typography variant="h5" fontWeight={600} sx={{ color: '#1e293b', mb: 0.5 }}>
                                Balancing State Data
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                {selectedState?.stateType} • {selectedState ? new Date(selectedState.createdAt).toLocaleDateString() : ''}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseBalancingStateDataModal}
                            sx={{
                                color: '#64748b',
                                '&:hover': {
                                    background: 'rgba(0,0,0,0.04)',
                                    color: '#1e293b'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Modal Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {selectedState?.data ? (
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem' }}>
                                    BALANCING STATE DATA (JSON)
                                </Typography>
                                <Box sx={{
                                    position: 'relative',
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: 3,
                                    p: 2.5,
                                    border: '1px solid rgba(0,0,0,0.04)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyStateData}
                                        sx={{
                                            position: 'absolute',
                                            top: '0.5rem',
                                            right: '0.5rem',
                                            color: copiedState ? '#4caf50' : '#666',
                                            backgroundColor: 'white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0,0,0,0.04)'
                                            }
                                        }}
                                        title={copiedState ? 'Copied!' : 'Copy to clipboard'}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                    <pre style={{
                                        margin: 0,
                                        fontSize: '0.8rem',
                                        lineHeight: 1.5,
                                        color: '#1e293b',
                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word'
                                    }}>
                                        {JSON.stringify(selectedState.data, null, 2).replace(/\n+$/, '')}
                                    </pre>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                    No data found for this balancing state.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}; 